/**
 * O modal do Negócio (11.4) — a moldura: véu sobre a tela de origem, caixa de 680 px no computador
 * e **tela cheia** no celular (até 768 px, a mesma virada de árvore do shell), `Esc` e o botão ×.
 *
 * - **Empilha um nível**: concluir tarefa, confirmar perda/ganho/exclusão e excluir nota abrem
 *   **sobre** ele como modal estreito (`Dialogo`); `Esc` fecha o de cima — o estreito marca o
 *   evento e este aqui o ignora.
 * - **Sair com alteração pendente pergunta uma vez** (compositor com texto, Dados alterados, nota em
 *   edição) — e "sair" é **qualquer navegação**: o ×, o `Esc`, o véu, o link da Empresa e o Voltar
 *   do navegador passam pelo mesmo bloqueio do roteador (`useBlocker`).
 * - O foco entra na caixa ao abrir, **não sai dela com Tab** (nem para o fundo) e volta a quem a
 *   abriu ao fechar; o fundo não rola; o véu só fecha se o clique **começou** nele.
 *
 * O conteúdo é `FichaDoNegocio`. Quem monta este modal é o layout (`?negocio=` sobre qualquer tela)
 * e a rota `/negocios/$negocioId` (link colado, sobre Hoje).
 */

import { useBlocker } from '@tanstack/react-router'
import { type KeyboardEvent, useCallback, useEffect, useRef } from 'react'

import { useEhCelular } from '@/shell/Shell'
import { Dialogo } from './Dialogo'
import { FichaDoNegocio } from './FichaDoNegocio'
import './modal-do-negocio.css'

export const PERGUNTA_DE_DESCARTE = 'Há alteração não salva neste negócio.'

const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface PropsDoModalDoNegocio {
  negocioId: string
  /** Fecha de fato — volta à tela de origem. Estável (`useCallback`) em quem monta. */
  aoFechar: () => void
  /** Aberto pelo endereço real (link colado): oferece "Ver no funil". */
  aoVerNoFunil?: (funilId: string) => void
}

export function ModalDoNegocio({ negocioId, aoFechar, aoVerNoFunil }: PropsDoModalDoNegocio) {
  const ehCelular = useEhCelular()
  const caixa = useRef<HTMLDivElement>(null)
  /* A pendência só é lida pelo bloqueio, na hora de uma navegação — ref, e não estado. */
  const pendenteAgora = useRef(false)
  const marcarPendencia = useCallback((pendente: boolean) => {
    pendenteAgora.current = pendente
  }, [])
  /** Excluído o Negócio, não há o que perguntar: a saída não é bloqueada. */
  const saindoSemPerguntar = useRef(false)
  const mousedownNoVeu = useRef(false)

  const bloqueio = useBlocker({
    shouldBlockFn: () => pendenteAgora.current && !saindoSemPerguntar.current,
    enableBeforeUnload: false,
    withResolver: true,
  })
  const confirmando = bloqueio.status === 'blocked'

  useEffect(() => {
    const anterior = document.activeElement instanceof HTMLElement ? document.activeElement : null
    caixa.current?.focus()
    const rolagemAnterior = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = rolagemAnterior
      anterior?.focus()
    }
  }, [])

  useEffect(() => {
    const aoTeclar = (evento: globalThis.KeyboardEvent) => {
      if (evento.key !== 'Escape' || evento.defaultPrevented || confirmando) {
        return
      }
      /* Um modal estreito aberto por cima é quem fecha com este `Esc`. */
      if (caixa.current?.querySelector('[role="dialog"]') !== null) {
        return
      }
      evento.preventDefault()
      aoFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [aoFechar, confirmando])

  /** O Tab dá a volta dentro do diálogo de cima (o estreito, se houver; senão, a caixa). */
  const prenderOFoco = (evento: KeyboardEvent<HTMLDivElement>) => {
    if (evento.key !== 'Tab' || caixa.current === null) {
      return
    }
    const estreitos = caixa.current.querySelectorAll<HTMLElement>('[role="dialog"]')
    const recinto = estreitos.item(estreitos.length - 1) ?? caixa.current
    const focaveis = [...recinto.querySelectorAll<HTMLElement>(FOCAVEIS)]
    const primeiro = focaveis[0]
    const ultimo = focaveis[focaveis.length - 1]
    if (primeiro === undefined || ultimo === undefined) {
      evento.preventDefault()
      return
    }
    const ativo = document.activeElement
    const dentro = ativo instanceof Node && recinto.contains(ativo)
    if (evento.shiftKey && (!dentro || ativo === primeiro || ativo === recinto)) {
      evento.preventDefault()
      ultimo.focus()
    } else if (!evento.shiftKey && (!dentro || ativo === ultimo)) {
      evento.preventDefault()
      primeiro.focus()
    }
  }

  return (
    <div
      className="modal-do-negocio__veu"
      data-formato={ehCelular ? 'celular' : 'mesa'}
      role="presentation"
      onMouseDown={(evento) => {
        mousedownNoVeu.current = evento.target === evento.currentTarget
      }}
      onClick={(evento) => {
        const comecouNoVeu = mousedownNoVeu.current
        mousedownNoVeu.current = false
        if (comecouNoVeu && evento.target === evento.currentTarget) {
          aoFechar()
        }
      }}
    >
      <div
        ref={caixa}
        className="modal-do-negocio"
        data-formato={ehCelular ? 'celular' : 'mesa'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-do-negocio-titulo"
        tabIndex={-1}
        onKeyDown={prenderOFoco}
      >
        <FichaDoNegocio
          negocioId={negocioId}
          aoFechar={aoFechar}
          aoExcluido={() => {
            saindoSemPerguntar.current = true
            aoFechar()
          }}
          aoEntregue={() => {
            /* Entregue a quem quem entregou nao enxerga (11.5): sai sem perguntar, como a
               exclusao. */
            saindoSemPerguntar.current = true
            aoFechar()
          }}
          aoMudarPendencia={marcarPendencia}
          aoVerNoFunil={aoVerNoFunil}
        />
      </div>
      {confirmando ? (
        <Dialogo
          titulo="Descartar alterações?"
          subtitulo={PERGUNTA_DE_DESCARTE}
          rotuloDoConfirmar="Descartar e sair"
          ocupado={false}
          erro={undefined}
          aoFechar={() => {
            bloqueio.reset()
            caixa.current?.focus()
          }}
          aoConfirmar={() => {
            saindoSemPerguntar.current = true
            bloqueio.proceed()
          }}
        >
          <p className="ficha__dialogo-aviso">
            O que foi digitado no compositor, numa nota ou nos dados do negócio e não foi salvo se
            perde.
          </p>
        </Dialogo>
      ) : null}
    </div>
  )
}
